// https://miro.com/app/board/uXjVJSBuPAo=/

// ---------------------------------------------------------------------------
//  WARNING: IMPORT ORDER MATTERS
//  WARNING: We have to support older browsers, which prevents us from using "await" at the top level (in this file)
// ---------------------------------------------------------------------------
import 'src/config/setup/polyfills.ts'
import '@sdui-hq/fonts/index.css'
import '@sdui-hq/icon-font/base.css'
import { initSentry } from '@/config/sentry.ts'
import { i18n } from 'src/config/i18n.ts'
import { createApp } from 'vue'
import registerPlugins from 'src/config/setup/plugins/index.js'
import registerMixins from 'src/config/setup/mixins/index.js'
import registerDirectives from 'src/config/setup/directives/index.js'
import registerComponents from 'src/config/setup/components/index.js'
import registerGlobalPrototypes from '@/config/setup/initGlobalPrototypes.js'
import store, { key } from '@/store/index.ts'
import router from 'src/router/router.js'
import axios from 'src/store/vue-axios/index.js'
import App from 'src/App.vue'
import native from 'src/config/native.js'
import { startStoreAutoAfterInitDispatcher, startStoreAutoInitDispatcher } from 'src/store/autoDispatcher.js'
import { routeToFirstTargetRouteAfterAppStart } from '@/router/routerUtils.js'

// ---------------------------------------------------------------------------
//  Create the Vue application
// ---------------------------------------------------------------------------
const app = createApp(App)

// ---------------------------------------------------------------------------
//  Global configuration
// ---------------------------------------------------------------------------
const isProduction = import.meta.env.MODE !== 'development'
app.config.performance = !isProduction

// ---------------------------------------------------------------------------
//  Register store, router, i18n plugins first (initialisation depends on it)
// ---------------------------------------------------------------------------
app.use(router)
app.use(store, key)
app.use(i18n)

// ---------------------------------------------------------------------------
//  Global $prototype properties
// ---------------------------------------------------------------------------
// Register global custom methods
registerGlobalPrototypes(app, axios, store, router)

// ---------------------------------------------------------------------------
//  Register plugins/mixins/directives/components
// ---------------------------------------------------------------------------
registerPlugins(app)
registerMixins(app)
registerDirectives(app)
registerComponents(app)

// ---------------------------------------------------------------------------
//  Initialize Sentry
// ---------------------------------------------------------------------------
initSentry(app)

// ---------------------------------------------------------------------------
//  Initialize Native states such as isNative, isAndroid, isIOS
// ---------------------------------------------------------------------------
native.initNativeStoreState(store)

// ---------------------------------------------------------------------------
//  Init critical data, such as user data (if logged in), language and branding styles before mounting the app
// ---------------------------------------------------------------------------
startStoreAutoInitDispatcher(store).then(() => {

  // Load and apply users language. If no user, browser language is used.
  store.dispatch('users/refreshCurrentLanguage').then(() => {

    // Load branding styles for users school if logged in
    store.dispatch('schoolBranding/loadStyles').then(() => {

      // Start store AFTER auto-init dispatcher. This will fetch non-critical data such as notification count etc
      startStoreAutoAfterInitDispatcher(store)

      // We can now route the user to their target page if given. This is done before mounting the app to execute any
      // possible middlewares that might be necessary for that route.
      routeToFirstTargetRouteAfterAppStart(router, store).then(() => {

        // ---------------------------------------------------------------------------
        //  App setup done, mount the app
        // ---------------------------------------------------------------------------
        app.mount('#app')
      })
    })
  })
})
